import * as React from 'react';
import { useTheme, Box, IconButton, Paper, Tooltip, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { Close as CloseIcon, Check as CheckIcon, FileDownload as FileDownloadIcon, OpenInNew as OpenInNewIcon, Error as ErrorIcon } from '@mui/icons-material';
import { useS3Manager } from '../../../../hooks/useS3Manager';
import { StyledSectionHeader } from '../../../../themes/styled-components';

interface ISnapshotsBronzeCard {
  snapshot?: ISnapshot;
  setIsDownloading: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function SnapshotsBronzeCard (props: ISnapshotsBronzeCard) {
  
  const theme = useTheme();
  const { downloadObject, listObjectsAtPrefix  } = useS3Manager({ bucketName: process.env.REACT_APP_AWS_S3_DATA_LAKE_BUCKET! }); 

  const RenderSchemaLinkColumn = (snapshot?: ISnapshot, item?: IManifestItem) => {

    if (!snapshot || !item || !snapshot.BronzeDb || !item?.BronzeTableName) return;

    const clickHandler = async () => {              

      if (!snapshot.BronzeDb || !item?.BronzeTableName) return;

      // Open SchemaView in new tab
      const url = `${window.location.origin}/schema?db=${encodeURIComponent(snapshot.BronzeDb)}&table=${encodeURIComponent(item.BronzeTableName)}`;        
      window.open(url, '_blank');

      // Example) Opening AWS console
      // window.open(
      //   `https://us-east-2.console.aws.amazon.com/glue/home?region=us-east-2#/v2/data-catalog/tables/view/${item.BronzeTableName}?database=${snapshot.BronzeDb}&catalogId=665302697606&versionId=latest`, 
      //   '_blank')
    };

    if (!item.IsCollectionComplete) {
      return (        
        <IconButton 
          aria-label="Open Bronze schema."
          sx={buttonStyle}
          disabled={!item.BronzeTableName}>
          <OpenInNewIcon sx={{ fontSize: 'medium' }}/>
        </IconButton>);
    } else {
      return (
        <Tooltip title="Open Bronze schema.">
          <IconButton 
            aria-label="Open Bronze schema."
            sx={buttonStyle}
            disabled={!item.BronzeTableName}
            onClick={clickHandler}>
            <OpenInNewIcon sx={{ fontSize: 'medium' }}/>
          </IconButton>
        </Tooltip>);
    }
  };

  const RenderBronzeDownloadColumn = (snapshot?: ISnapshot, item?: IManifestItem) => {
        
    if (!snapshot || !item) return;

    const clickHandler = async () => {              
      const prefix = `${process.env.REACT_APP_AWS_S3_DATA_LAKE_ROOT_PATH}/bronze/ama_${snapshot.ClientId}/snapshot=${snapshot.SnapshotDate}/data/${item.Category}`;
      const files = await listObjectsAtPrefix(prefix);
      const key = files.pop()?.BronzePath;
      if (!key) return;
      props.setIsDownloading(true);
      await downloadObject(key);
      props.setIsDownloading(false);
    };

    if (!item.IsCollectionComplete) {
      return (        
        <IconButton 
          aria-label="Download Bronze File."
          sx={buttonStyle}
          disabled={!item.IsCollectionComplete}
          onClick={clickHandler}>
          <FileDownloadIcon />
        </IconButton>);
    } else {
      return (
        <Tooltip title="Download Bronze File.">
          <IconButton 
            aria-label="Download Bronze File."
            sx={buttonStyle}
            disabled={!item.IsCollectionComplete}
            onClick={clickHandler}>
            <FileDownloadIcon />
          </IconButton>
        </Tooltip>);
    }
  };
  
  const RenderCollectedColumn = (item: IManifestItem) => {
    if (item.IsCollectionComplete) return (<CheckIcon sx={{ fontSize: '1rem', color: theme.palette.grey[500], borderColor: theme.palette.grey[400] }}/>);
    if (item.Error) {
      return (
        <Tooltip title={item.Error}>
          <ErrorIcon sx={{ fontSize: '1rem', color: theme.palette.grey[500], borderColor: theme.palette.grey[400] }}/>
        </Tooltip>
      );
    }

    return (<CloseIcon sx={{ fontSize: '1rem', color: theme.palette.grey[500], borderColor: theme.palette.grey[400] }}/>);
  };

  const tableHeaderStyle = {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem', 
    fontWeight: '900',
    height: '40px',
    padding: 0
  };
  
  const tableCellStyle = {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    height: '40px',
    padding: 0
  };

  const buttonStyle = {
    backgroundColor: 'white', 
    color: theme.palette.grey[500],
    borderColor: theme.palette.grey[400],    
    '&:hover': { 
      color: theme.palette.grey[500],
      backgroundColor: theme.palette.grey[100],
      borderColor: theme.palette.grey[400],   
    }, 
  };
  
  return (
    <Box sx={{ height: '100%' }}>
      <Paper sx={{ height: '100%', p: 2}}>
        <StyledSectionHeader>File Collection (Bronze)</StyledSectionHeader>
        <TableContainer sx={{ overflow: 'hidden' }}>
          <Table sx={{ width: '100%' }} aria-label="simple table">
            <TableHead>
              <TableRow>
                <TableCell align="left" style={{ width: 'auto', minWidth: '100px', ...tableHeaderStyle }}>File</TableCell>
                <TableCell align="center" style={{ width: 'auto', minWidth: '100px', ...tableHeaderStyle }}>Collected</TableCell>  
                <TableCell align="center" style={{ width: 'auto', minWidth: '100px', ...tableHeaderStyle }}>Schema</TableCell>  
                <TableCell align="center" style={{ width: 'auto', minWidth: '100px', ...tableHeaderStyle }}>Download</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props.snapshot?.SourceFiles?.map((item, index) => (
                <TableRow key={item.Category} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>                  
                  <TableCell align="left" style={{...tableCellStyle}}>{item.Category}</TableCell>    
                  <TableCell align="center" style={{...tableCellStyle}}>
                    {RenderCollectedColumn(item)}
                  </TableCell>
                  <TableCell align="center" style={{...tableCellStyle}}>
                    {RenderSchemaLinkColumn(props.snapshot, item)}
                  </TableCell>
                  <TableCell align="center" style={{...tableCellStyle}}>
                    {RenderBronzeDownloadColumn(props.snapshot, item)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}